import { Box, Stack, Typography } from '@mui/material';
import { ComponentProps } from 'react';

import {
  Logomark,
  LogomarkSize,
  LogomarkVariant,
} from '@/components/brand/Logomark/Logomark';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { PresentationBlockHoverRegion } from '@/modules/presentation/components/PresentationHoverActions/PresentationBlockHoverRegion';
import { Footer } from '@/modules/presentation/PresentationSlide/accessoryComponents';
import {
  DisclaimerFooter,
  DisclaimerFooterProps,
} from '@/modules/presentation/PresentationSlide/DisclaimerFooter';
import { useSlideFooterBranding } from '@/modules/presentation/PresentationSlide/SlideFooterBranding.context';
import { COLORS } from '@/styles/tokens/colors';

import { usePresentationConfiguration } from '../ClientPresentationDesigner.context';
import { useIsDesignMode } from '../ClientPresentationDesigner.hooks';
import { useSlideNumber } from '../context/ClientPresentationDesigner.paginationContext';
import { EditDisclaimerToolbarButton } from './EditDisclaimerToolbarButton';

const CLIENT_PRESENTATION_FOOTER_CONTENT_HEIGHT_PX = 40;
const CLIENT_PRESENTATION_FOOTER_LOGO_MAX_WIDTH_PX = 180;

export interface ClientPresentationFooterProps
  extends ComponentProps<typeof Footer> {
  slideId: string;
  hidePageNumber?: boolean;
  hideLogo?: boolean;
  variant?: DisclaimerFooterProps['variant'];
}

export function ClientPresentationFooter({
  hideDisclaimer,
  hidePageNumber,
  hideLogo,
  slideId,
  variant = 'standard',
}: ClientPresentationFooterProps) {
  const presentationConfiguration = usePresentationConfiguration();
  const { footerColor, useDarkLogo } = useSlideFooterBranding();

  // Only offer the hover "Edit disclaimer" affordance in the designer, and only
  // to users who can edit disclaimers. The hover region is inert outside the
  // designer's hover provider anyway, so this just avoids the extra wrapper.
  const isDesignMode = useIsDesignMode();
  const canEditDisclaimer = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_LEGAL_DISCLAIMERS_PAGE,
    // The footer also renders in the print/export flow, which has no
    // authenticated user; treat "no user" as "no edit access" so the hook
    // returns false instead of throwing.
    { detailed: false, returnFalseOnMissingUser: true }
  );
  const showEditDisclaimer = isDesignMode && canEditDisclaimer;

  const shouldDisplayLogo = !hideLogo;
  const shouldDisplayDisclaimer =
    !hideDisclaimer && presentationConfiguration?.showLegalDisclaimer;
  const shouldDisplayPageNumbers =
    !hidePageNumber && presentationConfiguration?.showPageNumbers;

  const pageNumber = useSlideNumber(slideId);

  // don't display anything on the cover if the page number & disclaimer are hidden,
  // otherwise the footer will just be a horizontal line with nothing below it
  if (
    variant === 'cover' &&
    !shouldDisplayPageNumbers &&
    !shouldDisplayDisclaimer
  ) {
    return null;
  }

  return (
    <Box sx={{ px: 2 }}>
      <Stack
        sx={{
          borderTop: `solid ${footerColor || COLORS.NEUTRAL_GRAY[200]} 1px`,
          pt: 2,
        }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Stack
          data-testid="client-presentation-footer-content"
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
          sx={{
            flex: 1,
            height: CLIENT_PRESENTATION_FOOTER_CONTENT_HEIGHT_PX,
            minWidth: 0,
          }}
        >
          {shouldDisplayLogo && (
            <Box
              data-testid="client-presentation-footer-logo"
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: '0 0 auto',
              }}
            >
              <Logomark
                style={{
                  maxHeight: CLIENT_PRESENTATION_FOOTER_CONTENT_HEIGHT_PX,
                  maxWidth: CLIENT_PRESENTATION_FOOTER_LOGO_MAX_WIDTH_PX,
                  width: 'auto',
                }}
                size={LogomarkSize.Wide}
                variant={
                  useDarkLogo
                    ? LogomarkVariant.Secondary
                    : LogomarkVariant.Primary
                }
              />
            </Box>
          )}
          {shouldDisplayDisclaimer &&
            (showEditDisclaimer ? (
              <PresentationBlockHoverRegion
                id={`${slideId}-footer-disclaimer`}
                sx={{ flex: 1, minWidth: 0 }}
                haloPadding
                renderToolbar={({ dismiss }) => (
                  <EditDisclaimerToolbarButton onClick={dismiss} />
                )}
              >
                <DisclaimerFooter
                  variant={variant}
                  sizing="client-presentation"
                  contentSx={footerColor ? { color: footerColor } : undefined}
                />
              </PresentationBlockHoverRegion>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DisclaimerFooter
                  variant={variant}
                  sizing="client-presentation"
                  contentSx={footerColor ? { color: footerColor } : undefined}
                />
              </Box>
            ))}
        </Stack>
        {shouldDisplayPageNumbers && (
          <Box data-testid="page-number">
            <Typography
              variant="title-xs"
              sx={{ color: footerColor || COLORS.NEUTRAL_GRAY[500] }}
            >
              {pageNumber}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
